import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ✅ Adjust this import path if your file location differs
// If your file is: src/assets/emp-logo.png
// and this JS file is inside src/..., then one of these will work:
// import empLogo from "../assets/emp-logo.png";
// import empLogo from "@/assets/emp-logo.png";
import empLogo from "../assets/emp-logo.png";

// --- PVWatts type dictionaries ---
const MODULE_TYPE_MAP = {
  0: "Standard",
  1: "Premium",
  2: "Thin film",
};

const ARRAY_TYPE_MAP = {
  0: "Fixed - Open Rack",
  1: "Fixed - Roof Mounted",
  2: "1-Axis",
  3: "1-Axis Backtracking",
  4: "2-Axis",
};

export const generatePvwattsReport = (params) => {
  const {
    MONTHS,
    monthlyProduction, // system total per month
    solarRadiationMonthly, // system average per month
    totalProduction, // system total annual kWh
    totalLoss,
    currentDesign,
    latitude,
    longitude,
    dcSystemSizeKw,
    arrayTilt,
    arrayAzimuth,

    requestedLocation,
    weatherDataSource,
    weatherDistanceMiles,
    moduleType,
    arrayType,
    dcToAcRatio,
    inverterEfficiency,
    groundCoverageRatio,
    albedo,
    bifacial,
    monthlyIrradianceLoss = [],
    dcCapacityFactor,

    // detailed loss breakdown from UI
    lossBreakdown = {},

    // raw per-segment PVWatts responses
    segmentProductions = [],

    // matching request params per segment (what you sent to PVWatts)
    segmentRequestParams = [],

    // per-segment monthly irradiance loss: [[12], [12], ...] (%)
    segmentMonthlyIrradianceLoss = [],
  } = params;

  const doc = new jsPDF("p", "pt", "letter");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const leftMargin = 36;
  const rightMargin = 36;
  const rightColumnX = leftMargin; // use full width, no left column
  const contentWidth = pageWidth - leftMargin - rightMargin;

  const primaryBlue = [0, 102, 204];
  const accentOrange = [245, 130, 32];

  // ---------- helpers ----------
  const safeFixed = (value, digits = 2) =>
    typeof value === "number" && !Number.isNaN(value)
      ? value.toFixed(digits)
      : "N/A";

  const safeInt = (value) =>
    typeof value === "number" && !Number.isNaN(value)
      ? value.toFixed(0)
      : "N/A";

  const safePercent = (value, digits = 0) =>
    typeof value === "number" && !Number.isNaN(value)
      ? `${value.toFixed(digits)}%`
      : "N/A";

  const formatLat = (lat) => {
    if (typeof lat !== "number" || Number.isNaN(lat)) return "N/A";
    const hemi = lat >= 0 ? "N" : "S";
    return `${Math.abs(lat).toFixed(2)}° ${hemi}`;
  };

  const formatLng = (lng) => {
    if (typeof lng !== "number" || Number.isNaN(lng)) return "N/A";
    const hemi = lng >= 0 ? "E" : "W";
    return `${Math.abs(lng).toFixed(2)}° ${hemi}`;
  };

  const printParagraph = (text, x, y, width, fontSize = 7) => {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, width);
    doc.text(lines, x, y);
    const lineHeight = fontSize + 1.5;
    return y + lines.length * lineHeight;
  };

  const sectionHeader = (title, y) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...primaryBlue);
    doc.text(title, rightColumnX, y);
    y += 4;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.7);
    doc.line(rightColumnX, y, pageWidth - rightMargin, y);
    doc.setTextColor(0, 0, 0);
    return y + 14;
  };

  const infoRow = (label, value, y) => {
    const labelX = rightColumnX;
    const valueX = rightColumnX + 165;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(label, labelX, y);
    doc.setFont("helvetica", "bold");
    doc.text(value || "N/A", valueX, y);
    return y + 11;
  };

  const asNumberOrNull = (v) =>
    typeof v === "number" && !Number.isNaN(v) ? v : null;

  // ✅ Logo helper (keeps your image aspect ratio 975×173)
  const addHeaderLogo = () => {
    // Chosen to be “not too big, not too small”
    const logoW = 110; // pt
    const logoH = (logoW * 173) / 975; // keep aspect ratio (~19.5pt)
    const logoX = leftMargin;
    const logoY = 20; // top padding

    try {
      doc.addImage(empLogo, "PNG", logoX, logoY, logoW, logoH);
    } catch (e) {
      // If the bundler returns a URL and jsPDF can’t read it directly,
      // you may need to convert it to base64 before calling addImage.
      // Keeping silent so report generation doesn't crash.
    }

    return { logoW, logoH, logoX, logoY };
  };

  // Common annual output card (system + segments)
  const drawAnnualOutputCard = (x, y, width, annualText) => {
    const cardHeight = 60;

    doc.setDrawColor(220, 220, 220);
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(x, y, width, cardHeight, 4, 4, "FD");

    // Title
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text("Estimated Annual Output", x + 8, y + 16);

    // Value
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryBlue);
    doc.text(annualText || "N/A", x + 8, y + 36);

    // Disclaimer inside the card
    const disclaimer =
      "System output may vary based on actual conditions at this location.";
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    const wrapped = doc.splitTextToSize(disclaimer, width - 16);
    doc.text(wrapped, x + 8, y + 48);

    return y + cardHeight;
  };

  // Draw a nicer bar chart for monthly AC energy (same data as UI)
  const drawMonthlyBarChart = (labels, values, x, yTop, width, height) => {
    if (
      !Array.isArray(labels) ||
      !Array.isArray(values) ||
      labels.length === 0 ||
      values.length === 0
    ) {
      return yTop;
    }

    const numericVals = values.map((v) =>
      typeof v === "number" && !Number.isNaN(v) ? v : 0
    );
    const hasPositive = numericVals.some((v) => v > 0);
    if (!hasPositive) return yTop;

    const maxValRaw = Math.max(...numericVals);
    if (maxValRaw <= 0) return yTop;

    // Round max up to a "nice" number (like 500, 1000, etc.)
    const order = Math.pow(10, Math.floor(Math.log10(maxValRaw)));
    const niceMax = Math.ceil(maxValRaw / order) * order;

    const chartHeight = height;
    const chartWidth = width;
    const paddingLeft = 26; // space for y-axis labels
    const paddingBottom = 18; // space for x-axis labels
    const innerX = x + paddingLeft;
    const innerYTop = yTop;
    const innerHeight = chartHeight - paddingBottom;
    const innerWidth = chartWidth - paddingLeft;

    const xAxisY = innerYTop + innerHeight;
    const barCount = labels.length;
    const gap = 3;
    const barWidth = (innerWidth - gap * (barCount + 1)) / barCount;

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Monthly AC Energy (kWh)", innerX, yTop - 6);

    // Axes
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    // y-axis
    doc.line(innerX, innerYTop, innerX, xAxisY);
    // x-axis
    doc.line(innerX, xAxisY, innerX + innerWidth, xAxisY);

    // Y-axis ticks & gridlines
    const tickCount = 4; // 0, 1/3, 2/3, max
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    for (let i = 0; i <= tickCount; i++) {
      const fraction = i / tickCount;
      const val = niceMax * fraction;
      const yTick = xAxisY - fraction * innerHeight;

      // tick line on y-axis
      doc.setDrawColor(0, 0, 0);
      doc.line(innerX - 2, yTick, innerX, yTick);

      // gridline
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(innerX, yTick, innerX + innerWidth, yTick);

      // label
      const label = `${val.toFixed(0)}`;
      doc.setTextColor(0, 0, 0);
      doc.text(label, innerX - 4, yTick + 2, { align: "right" });
    }

    // Bars + x-axis labels
    for (let i = 0; i < barCount; i++) {
      const val = numericVals[i];
      const barHeight =
        (val / niceMax) * (innerHeight - 4); // a little top padding
      const bx = innerX + gap + i * (barWidth + gap);
      const by = xAxisY - barHeight;

      // bar (filled)
      doc.setFillColor(...primaryBlue);
      doc.rect(bx, by, barWidth, barHeight, "F");

      // value label above bar (optional, small)
      if (val > 0) {
        doc.setFontSize(6);
        doc.setTextColor(40, 40, 40);
        doc.text(val.toFixed(0), bx + barWidth / 2, by - 2, {
          align: "center",
        });
      }

      // month label on x-axis
      const monthLabel = labels[i] || "";
      doc.setFontSize(6);
      doc.setTextColor(0, 0, 0);
      doc.text(monthLabel, bx + barWidth / 2, xAxisY + 8, {
        align: "center",
      });
    }

    return xAxisY + 20; // Y position to continue content below chart
  };

// ---------- header ----------
const now = new Date();
const dateStr = (() => {
  const datePart = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(now);

  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(now);

  return `${datePart} - ${timePart}`;
})();

// Example output:
// "Thu, Dec 18, 2025 - 01:35 PM"

// Example: "Thu, Dec 18, 2025, 01:35 PM"


// Add logo on the first page immediately
const { logoH, logoX, logoY } = addHeaderLogo();

// ✅ Date/time: move UNDER the logo (only on first page)
doc.setFont("helvetica", "normal");
doc.setFontSize(9);

const dateX = logoX;
const gap = 6; // little gap under the image
const dateY = logoY + logoH + gap + 9; // +9 to match text baseline nicely

doc.text(dateStr, dateX, dateY);

// doc.setFontSize(11);
// doc.text("PVWatts Calculator", pageWidth / 2, 26, { align: "center" });

doc.setFontSize(18);
doc.setFont("helvetica", "bold");
doc.setTextColor(0, 0, 0);

  const annualKWh =
    typeof totalProduction === "number" && !Number.isNaN(totalProduction)
      ? Math.round(totalProduction).toLocaleString()
      : "N/A";

  // ---------- RESULTS label ----------
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...accentOrange);
  doc.text("RESULTS", rightColumnX + 10, 110);
  doc.setTextColor(0, 0, 0);

  // ---------- SYSTEM-LEVEL TABLE + CARD + CHART ----------
  const systemTopY = 120;
  const midX = leftMargin + contentWidth / 2;

  // Build month rows for system table
  const monthRows = (MONTHS || []).map((m, i) => {
    const rad = solarRadiationMonthly?.[i];
    const prod = monthlyProduction?.[i];
    return [
      m || "",
      typeof rad === "number" && !Number.isNaN(rad) ? rad.toFixed(2) : "N/A",
      typeof prod === "number" && !Number.isNaN(prod) ? prod.toFixed(0) : "N/A",
    ];
  });

  let annualSolar = "N/A";
  if (Array.isArray(solarRadiationMonthly) && solarRadiationMonthly.length) {
    const vals = solarRadiationMonthly.filter(
      (v) => typeof v === "number" && !Number.isNaN(v)
    );
    if (vals.length) {
      const sum = vals.reduce((a, b) => a + b, 0);
      annualSolar = (sum / vals.length).toFixed(2);
    }
  }
  const annualRow = [
    "Annual",
    annualSolar,
    annualKWh !== "N/A" ? annualKWh.replace(/,/g, "") : "N/A",
  ];
  monthRows.push(annualRow);

  // TABLE on LEFT (system)
  autoTable(doc, {
    startY: systemTopY,
    margin: { left: rightColumnX, right: rightMargin },
    tableWidth: contentWidth / 2 - 6, // left half
    theme: "grid",
    head: [["Month", "Solar Radiation\n(kWh / m² / day)", "AC Energy\n(kWh)"]],
    body: monthRows,
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 3,
      valign: "middle",
    },
    headStyles: {
      fontStyle: "bold",
      halign: "center",
      textColor: primaryBlue,
      fillColor: [255, 255, 255],
      lineWidth: 0.5,
    },
    bodyStyles: {
      halign: "right",
    },
    columnStyles: {
      0: { halign: "left", cellWidth: 60 },
      1: { cellWidth: 90 },
      2: { cellWidth: 70 },
    },
    didParseCell: (data) => {
      if (
        data.section === "body" &&
        data.row.index === monthRows.length - 1
      ) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.textColor = accentOrange;
      }
    },
  });

  const systemTableBottomY = doc.lastAutoTable.finalY;

  // CARD + CHART on RIGHT (system), aligned to right of table
  const systemChartX = midX + 6;
  const systemChartWidth = contentWidth / 2 - 6;

  const annualSystemText =
    annualKWh !== "N/A" ? `${annualKWh} kWh/Year*` : "N/A";

  // Card at top of the right block
  const systemCardBottomY = drawAnnualOutputCard(
    systemChartX,
    systemTopY,
    systemChartWidth,
    annualSystemText
  );

  // Chart under the card, still on the right side
  const systemChartBottomY = drawMonthlyBarChart(
    MONTHS,
    monthlyProduction,
    systemChartX,
    systemCardBottomY + 40,
    systemChartWidth,
    90
  );

  let y = Math.max(systemTableBottomY, systemChartBottomY) + 18;

  // ---------- Location and Station Identification ----------
  y = sectionHeader("Location and Station Identification", y);

  const addr = requestedLocation || currentDesign?.address || "N/A";

  const latSrc =
    typeof latitude === "number" && !Number.isNaN(latitude)
      ? latitude.toFixed(2)
      : "N/A";
  const lngSrc =
    typeof longitude === "number" && !Number.isNaN(longitude)
      ? longitude.toFixed(2)
      : "N/A";

  const weatherLine =
    weatherDataSource ||
    (latSrc !== "N/A" && lngSrc !== "N/A"
      ? `Lat, Lng: ${latSrc}, ${lngSrc}${
          typeof weatherDistanceMiles === "number"
            ? ` ${weatherDistanceMiles.toFixed(1)} mi`
            : ""
        }`
      : "N/A");

  y = infoRow("Requested Location", addr, y);
  y = infoRow("Weather Data Source", weatherLine, y);
  y = infoRow("Latitude", formatLat(latitude), y);
  y = infoRow("Longitude", formatLng(longitude), y);

  y += 8;

  // ---------- PV System Specifications ----------
  y = sectionHeader("PV System Specifications", y);

  const dcSizeStr =
    typeof dcSystemSizeKw === "number" && !Number.isNaN(dcSystemSizeKw)
      ? `${dcSystemSizeKw.toFixed(1)} kW`
      : "N/A";

  // record top of this block so we can align loss table to the right
  const pvSpecsTopY = y;

  y = infoRow("DC System Size", dcSizeStr, y);

  const moduleTypeDisplay =
    moduleType !== undefined && moduleType !== null
      ? MODULE_TYPE_MAP[moduleType] != null
        ? `${moduleType} - ${MODULE_TYPE_MAP[moduleType]}`
        : String(moduleType)
      : "N/A";

  const arrayTypeDisplay =
    arrayType !== undefined && arrayType !== null
      ? ARRAY_TYPE_MAP[arrayType] != null
        ? `${arrayType} - ${ARRAY_TYPE_MAP[arrayType]}`
        : String(arrayType)
      : "N/A";

  y = infoRow("Module Type", moduleTypeDisplay, y);
  y = infoRow("Array Type", arrayTypeDisplay, y);

  y = infoRow(
    "System Losses",
    typeof totalLoss === "number" && !Number.isNaN(totalLoss)
      ? `${totalLoss.toFixed(2)}%`
      : "N/A",
    y
  );
  y = infoRow(
    "Array Tilt",
    typeof arrayTilt === "number" && !Number.isNaN(arrayTilt)
      ? `${arrayTilt.toFixed(0)}°`
      : "N/A",
    y
  );
  y = infoRow(
    "Array Azimuth",
    typeof arrayAzimuth === "number" && !Number.isNaN(arrayAzimuth)
      ? `${arrayAzimuth.toFixed(0)}°`
      : "N/A",
    y
  );
  y = infoRow(
    "DC to AC Size Ratio",
    typeof dcToAcRatio === "number" && !Number.isNaN(dcToAcRatio)
      ? dcToAcRatio.toFixed(1)
      : "N/A",
    y
  );
  y = infoRow(
    "Inverter Efficiency",
    typeof inverterEfficiency === "number"
      ? safePercent(inverterEfficiency)
      : "N/A",
    y
  );
  y = infoRow(
    "Ground Coverage Ratio",
    typeof groundCoverageRatio === "number" &&
      !Number.isNaN(groundCoverageRatio)
      ? groundCoverageRatio.toFixed(1)
      : "N/A",
    y
  );
  y = infoRow(
    "Albedo",
    typeof albedo === "number" && !Number.isNaN(albedo)
      ? albedo.toFixed(2)
      : albedo || "N/A",
    y
  );
  y = infoRow(
    "Bifacial",
    typeof bifacial === "number" && !Number.isNaN(bifacial)
      ? bifacial === 0
        ? "No (0)"
        : `Yes (${bifacial.toFixed(2)})`
      : bifacial || "N/A",
    y
  );

  const pvSpecsBottomY = y;

  // ---------- System Losses Table (right side) ----------
  const toNum = (v) =>
    typeof v === "number"
      ? v
      : typeof v === "string"
      ? Number(v)
      : NaN;

  const lb = lossBreakdown || {};

  const lossRows = [
    ["Module nameplate rating", safeFixed(toNum(lb.moduleNameplateRating), 1), "%"],
    ["Mismatch", safeFixed(toNum(lb.mismatch), 1), "%"],
    ["Connections", safeFixed(toNum(lb.connections), 1), "%"],
    [
      "Light-induced degradation",
      safeFixed(toNum(lb.lightInducedDegradation), 1),
      "%",
    ],
    ["Wiring", safeFixed(toNum(lb.wiring), 1), "%"],
    ["Soiling", safeFixed(toNum(lb.soiling), 1), "%"],
    ["Availability", safeFixed(toNum(lb.availability), 1), "%"],
    ["Age", safeFixed(toNum(lb.age), 1), "%"],
    ["Snow", safeFixed(toNum(lb.snow), 1), "%"],
    ["Other", safeFixed(toNum(lb.other), 1), "%"],
    [
      "Total Loss",
      typeof totalLoss === "number" && !Number.isNaN(totalLoss)
        ? totalLoss.toFixed(2)
        : "N/A",
      "%",
    ],
  ];

  const lossTableX = rightColumnX + 280; // push to the right side
  const lossTableWidth = pageWidth - rightMargin - lossTableX;

  autoTable(doc, {
    startY: pvSpecsTopY,
    margin: { left: lossTableX, right: rightMargin },
    tableWidth: lossTableWidth,
    theme: "grid",
    head: [["System Losses", "Value", "Unit"]],
    body: lossRows,
    styles: {
      font: "helvetica",
      fontSize: 7,
      cellPadding: 3,
      valign: "middle",
    },
    headStyles: {
      fontStyle: "bold",
      halign: "center",
      textColor: primaryBlue,
      fillColor: [255, 255, 255],
      lineWidth: 0.5,
    },
    bodyStyles: {
      halign: "right",
    },
    columnStyles: {
      0: { halign: "left", cellWidth: 120 },
      1: { cellWidth: 40 },
      2: { cellWidth: 30 },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === lossRows.length - 1) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.textColor = accentOrange;
      }
    },
  });

  const lossTableBottomY =
    doc.lastAutoTable && doc.lastAutoTable.finalY
      ? doc.lastAutoTable.finalY
      : pvSpecsBottomY;

  // continue below whichever block is taller
  y = Math.max(pvSpecsBottomY, lossTableBottomY) + 6;

  // ---------- Monthly Irradiance Loss (system) ----------
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text("Monthly Irradiance Loss", rightColumnX, y);
  y += 12;

  const monthsRow1 = ["Jan", "Feb", "Mar", "Apr", "May", "June"];
  const monthsRow2 = ["July", "Aug", "Sept", "Oct", "Nov", "Dec"];
  const baseX = rightColumnX + 105;
  const stepX = 35;

  const pctFor = (idx) => {
    const v = monthlyIrradianceLoss?.[idx];
    return typeof v === "number" && !Number.isNaN(v)
      ? `${v.toFixed(0)}%`
      : "N/A";
  };

  monthsRow1.forEach((m, i) => {
    doc.text(m, baseX + stepX * i, y);
  });
  y += 11;
  monthsRow1.forEach((_, i) => {
    doc.setFont("helvetica", "bold");
    doc.text(pctFor(i), baseX + stepX * i, y);
  });

  y += 16;
  doc.setFont("helvetica", "normal");
  monthsRow2.forEach((m, i) => {
    doc.text(m, baseX + stepX * i, y);
  });
  y += 11;
  monthsRow2.forEach((_, i) => {
    doc.setFont("helvetica", "bold");
    doc.text(pctFor(i + 6), baseX + stepX * i, y);
  });

  y += 22;

  // ---------- Performance Metrics ----------
  y = sectionHeader("Performance Metrics", y);

  const dcCapStr =
    typeof dcCapacityFactor === "number"
      ? `${dcCapacityFactor.toFixed(1)}%`
      : "N/A";

  y = infoRow("DC Capacity Factor", dcCapStr, y);

  // ============================================================
  //         PER-SEGMENT BREAKDOWN
  // ============================================================
  if (Array.isArray(segmentProductions) && segmentProductions.length > 0) {
    const bottomMargin = 60;
    const newPageYStart = 80;

    const ensureSpace = (minSpace = 200) => {
      if (y + minSpace > pageHeight - bottomMargin) {
        doc.addPage();
        // NOTE: we don't need to draw logo here because we add it for all pages
        // in the final footer loop (below). Keeps logic simple and consistent.
        y = newPageYStart;
      }
    };

    y += 12;
    // y = sectionHeader("Segment Breakdowns", y);

    segmentProductions.forEach((segProd, idx) => {
      const prod = segProd?.production || {};
      const inputs = prod.inputs || {};
      const req = segmentRequestParams[idx] || {};
      const outputs = prod.outputs || {};

      ensureSpace(240);

      const segName = `Segment ${idx + 1}`;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...accentOrange);
      doc.text(segName, leftMargin, y);
      doc.setTextColor(0, 0, 0);

      const segAnnual = asNumberOrNull(outputs.ac_annual);
      const segAnnualDisplay =
        segAnnual != null
          ? `${Math.round(segAnnual).toLocaleString()} kWh/Year*`
          : "N/A";

      y += 18;

      const segTopY = y;
      const midXSeg = leftMargin + contentWidth / 2;
      const segChartX = midXSeg + 6;
      const segChartWidth = contentWidth / 2 - 6;

      const segAcMonthly = outputs.ac_monthly || [];
      const segSolradMonthly = outputs.solrad_monthly || [];

      const segMonthRows = (MONTHS || []).map((m, i) => {
        const rad = segSolradMonthly?.[i];
        const prodVal = segAcMonthly?.[i];
        return [
          m || "",
          typeof rad === "number" && !Number.isNaN(rad)
            ? rad.toFixed(2)
            : "N/A",
          typeof prodVal === "number" && !Number.isNaN(prodVal)
            ? prodVal.toFixed(0)
            : "N/A",
        ];
      });

      let segAnnualSolar = "N/A";
      if (Array.isArray(segSolradMonthly) && segSolradMonthly.length) {
        const vals = segSolradMonthly.filter(
          (v) => typeof v === "number" && !Number.isNaN(v)
        );
        if (vals.length) {
          const sum = vals.reduce((a, b) => a + b, 0);
          segAnnualSolar = (sum / vals.length).toFixed(2);
        }
      }

      const segAnnualKwh =
        segAnnual != null
          ? segAnnual.toFixed(0)
          : (() => {
              const valid = (segAcMonthly || []).filter(
                (v) => typeof v === "number" && !Number.isNaN(v)
              );
              if (!valid.length) return "N/A";
              const sum = valid.reduce((a, b) => a + b, 0);
              return sum.toFixed(0);
            })();

      const segAnnualRow = ["Annual", segAnnualSolar, segAnnualKwh];
      segMonthRows.push(segAnnualRow);

      autoTable(doc, {
        startY: segTopY,
        margin: { left: rightColumnX, right: rightMargin },
        tableWidth: contentWidth / 2 - 6,
        theme: "grid",
        head: [
          ["Month", "Solar Radiation\n(kWh / m² / day)", "AC Energy\n(kWh)"],
        ],
        body: segMonthRows,
        styles: {
          font: "helvetica",
          fontSize: 8,
          cellPadding: 3,
          valign: "middle",
        },
        headStyles: {
          fontStyle: "bold",
          halign: "center",
          textColor: primaryBlue,
          fillColor: [255, 255, 255],
          lineWidth: 0.5,
        },
        bodyStyles: {
          halign: "right",
        },
        columnStyles: {
          0: { halign: "left", cellWidth: 60 },
          1: { cellWidth: 90 },
          2: { cellWidth: 70 },
        },
        didParseCell: (data) => {
          if (
            data.section === "body" &&
            data.row.index === segMonthRows.length - 1
          ) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = accentOrange;
          }
        },
      });

      const segTableBottomY = doc.lastAutoTable.finalY;

      const segCardBottomY = drawAnnualOutputCard(
        segChartX,
        segTopY,
        segChartWidth,
        segAnnualDisplay
      );

      const segChartData = MONTHS.map(
        (_, i) => (typeof segAcMonthly[i] === "number" ? segAcMonthly[i] : 0)
      );

      const segChartBottomY = drawMonthlyBarChart(
        MONTHS,
        segChartData,
        segChartX,
        segCardBottomY + 40,
        segChartWidth,
        90
      );

      y = Math.max(segTableBottomY, segChartBottomY) + 16;

      // -------- Segment PV System Specifications --------
      ensureSpace(140);
      y = sectionHeader("PV System Specifications", y);

      const numOr = (a, b) => {
        const tryA = parseFloat(a);
        if (!Number.isNaN(tryA)) return tryA;
        const tryB = parseFloat(b);
        return !Number.isNaN(tryB) ? tryB : NaN;
      };

      const dcSizeVal = numOr(inputs.system_capacity, req.capacity);
      const dcSizeSegStr = !Number.isNaN(dcSizeVal)
        ? `${dcSizeVal.toFixed(2)} kW`
        : "N/A";
      y = infoRow("DC System Size", dcSizeSegStr, y);

      const lossesVal = numOr(inputs.losses, req.losses);
      const lossesStr = !Number.isNaN(lossesVal)
        ? `${lossesVal.toFixed(2)}%`
        : "N/A";
      y = infoRow("System Losses", lossesStr, y);

      const tiltVal = numOr(inputs.tilt, req.tilt);
      const tiltStr = !Number.isNaN(tiltVal) ? `${tiltVal.toFixed(0)}°` : "N/A";
      y = infoRow("Array Tilt", tiltStr, y);

      const azVal = numOr(inputs.azimuth, req.azimuth);
      const azStr = !Number.isNaN(azVal) ? `${azVal.toFixed(0)}°` : "N/A";
      y = infoRow("Array Azimuth", azStr, y);

      const dcAcVal = numOr(inputs.dc_ac_ratio, req.dc_ac_ratio);
      const dcAcStr = !Number.isNaN(dcAcVal) ? dcAcVal.toFixed(2) : "N/A";
      y = infoRow("DC to AC Size Ratio", dcAcStr, y);

      const invEffVal = numOr(inputs.inv_eff, req.inv_eff);
      const invEffStr = !Number.isNaN(invEffVal)
        ? safePercent(invEffVal)
        : "N/A";
      y = infoRow("Inverter Efficiency", invEffStr, y);

      const gcrVal = numOr(inputs.gcr, req.gcr);
      const gcrStr = !Number.isNaN(gcrVal) ? gcrVal.toFixed(2) : "N/A";
      y = infoRow("Ground Coverage Ratio", gcrStr, y);

      const albedoVal = numOr(inputs.albedo, req.albedo);
      const albedoStr = !Number.isNaN(albedoVal)
        ? albedoVal.toFixed(2)
        : "N/A";
      y = infoRow("Albedo", albedoStr, y);

      const bifRaw =
        inputs.bifaciality ??
        inputs.bifacial ??
        req.bifaciality ??
        req.bifacial;
      let bifStr = "N/A";
      const bifValNum = parseFloat(bifRaw);
      if (!Number.isNaN(bifValNum)) {
        bifStr = bifValNum === 0 ? "No (0)" : `Yes (${bifValNum.toFixed(2)})`;
      }
      y = infoRow("Bifacial", bifStr, y);

      y += 6;

      // Monthly Irradiance Loss grid for segment
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text("Monthly Irradiance Loss", rightColumnX, y);
      y += 12;

      const segBaseX = rightColumnX + 105;
      const segLossArr = segmentMonthlyIrradianceLoss[idx] || [];

      const segPctFor = (monthIdx) => {
        const v = segLossArr[monthIdx];
        return typeof v === "number" && !Number.isNaN(v)
          ? `${v.toFixed(0)}%`
          : "N/A";
      };

      monthsRow1.forEach((m, i) => {
        doc.text(m, segBaseX + stepX * i, y);
      });
      y += 11;
      monthsRow1.forEach((_, i) => {
        doc.setFont("helvetica", "bold");
        doc.text(segPctFor(i), segBaseX + stepX * i, y);
      });

      y += 16;
      doc.setFont("helvetica", "normal");
      monthsRow2.forEach((m, i) => {
        doc.text(m, segBaseX + stepX * i, y);
      });
      y += 11;
      monthsRow2.forEach((_, i) => {
        doc.setFont("helvetica", "bold");
        doc.text(segPctFor(i + 6), segBaseX + stepX * i, y);
      });

      y += 22;
    });
  }

// ---------- footer on all pages ----------
const pageCount = doc.internal.getNumberOfPages();
for (let i = 1; i <= pageCount; i++) {
  doc.setPage(i);

  // Add logo to every page (top-left)
  addHeaderLogo();

  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const footerY = ph - 26;

  // ✅ Bold black separator line where footer starts
  const footerLineY = footerY - 26; // move line a bit above footer text
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(1.6);
  doc.line(leftMargin, footerLineY, pw - rightMargin, footerLineY);

  // ✅ Footer text (nice disclaimer + rights)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);

  const footerNote =
    "This report is generated using PVWatts® (NREL) data and estimates. Results may vary based on actual site conditions. All rights reserved © Empower Energy Solutions.";
  const wrappedNote = doc.splitTextToSize(footerNote, pw - leftMargin - rightMargin);

  // place note just under the line
  doc.text(wrappedNote, leftMargin, footerLineY + 14);

  // keep your existing PVWatts URL + page number at the very bottom
  doc.setFontSize(8);
  doc.text("https://pvwatts.nrel.gov/pvwatts.php", leftMargin, footerY);
  doc.text(`${i}/${pageCount}`, pw - rightMargin, footerY, { align: "right" });
}


  doc.save("PV_Production_Report.pdf");
};